import * as R from 'remeda';
import { isEmbedded } from '../constants/config';

export const LOCALE_LOCALSTORAGE_KEY = 'locale';

/**
 * Form-only sentinel used by locale dropdowns to represent
 * "no explicit user preference". On submit, callers must map it to
 * `null` in the PATCH payload so the BE clears the stored locale.
 */
export const LOCALE_DEFAULT_SENTINEL = '__default__';

export const fallbackLocale = 'en';
export const defaultLocaleKeys = [
  fallbackLocale,
  'cs',
  'de',
  'es',
  'fr',
  'ja',
  'pt-br',
  'zh',
] as const;
const additionalLocaleKeys = [
  'da',
  'fi',
  'hr',
  'hu',
  'it',
  'nb',
  'nl',
  'pl',
  'pt',
  'sl',
  'sq',
  'sr',
  'sv',
  'tr',
] as const;

export const supportedLocales = [...defaultLocaleKeys, ...additionalLocaleKeys];

export type IntlLocale = (typeof supportedLocales)[number];

type LocaleAvailabilityOptions = {
  additionalLocalesEnabled?: boolean;
};

export const getAvailableLocales = ({
  additionalLocalesEnabled = false,
}: LocaleAvailabilityOptions = {}) =>
  isEmbedded() || additionalLocalesEnabled
    ? supportedLocales
    : defaultLocaleKeys;

const availableLocales = getAvailableLocales();

const browserLocale = window.navigator.languages.reduce<string | undefined>(
  (bestLocale, preferredLocale) =>
    bestLocale ||
    R.pipe(
      supportedLocales,
      a => (Array.isArray(a) ? a : [a]),
      R.find(supportedLocale =>
        preferredLocale.toLowerCase().includes(supportedLocale)
      )
    ),
  undefined
);

const getAvailableLocaleKey = (
  locale: string | undefined | null
): IntlLocale | undefined => {
  if (!locale) return undefined;

  return availableLocales.find(
    availableLocale => availableLocale.toLowerCase() === locale.toLowerCase()
  );
};

export const getDefaultLocale = ({
  omitStorage,
}: { omitStorage?: boolean } = {}): IntlLocale =>
  getAvailableLocaleKey(
    omitStorage ? undefined : localStorage.getItem(LOCALE_LOCALSTORAGE_KEY)
  ) ||
  getAvailableLocaleKey(browserLocale) ||
  fallbackLocale;

export const getLocaleKey = (
  locale: string | undefined | null
): IntlLocale | undefined => {
  if (!locale) return undefined;

  return supportedLocales.find(
    supportedLocale => supportedLocale.toLowerCase() === locale.toLowerCase()
  );
};

const localeLabels: Record<IntlLocale, string> = {
  cs: 'Czech',
  de: 'German',
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  ja: 'Japanese',
  zh: 'Chinese Simplified',
  'pt-br': 'Portuguese (Brazil)',
  da: 'Danish',
  fi: 'Finnish',
  hr: 'Croatian',
  hu: 'Hungarian',
  it: 'Italian',
  nb: 'Norwegian Bokmål',
  nl: 'Dutch',
  pl: 'Polish',
  pt: 'Portuguese',
  sl: 'Slovenian',
  sq: 'Albanian',
  sr: 'Serbian',
  sv: 'Swedish',
  tr: 'Turkish',
};

export const getLocaleOptions = (options?: LocaleAvailabilityOptions) =>
  R.sortBy(
    getAvailableLocales(options).map(value => {
      const label =
        localeLabels[value] ||
        // oxlint-disable-next-line no-console
        console.warn(`Locale label missing for ${value}`) ||
        value;

      return { value, label };
    }),
    option => option.label
  );

export type LocaleOption = ReturnType<typeof getLocaleOptions>[number];
